import { BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import { defineStore } from 'pinia'
import { computed, inject, ref } from 'vue'
import { PLATFORM_ADMIN_ROLES } from '@/core/constants/allowed-roles.constant'
import {
  createUtrakkMsalLoginRequest,
  resolveUtrakkLoginRedirectUri,
  UTRAKK_AUTH_PROVIDER,
  UTRAKK_DOMAIN_HINT,
} from '@/modules/auth/services/utrakk-idp-login.service'
import { deleteCookie } from '@/modules/new-bradley/utils/cookies.js'
import { useBusinessStructureStore } from '@/modules/tenant-admin/stores/tenant-admin-business-structure.store'
import { usePhysicalStructureStore } from '@/modules/tenant-admin/stores/tenant-admin-physical-structure.store'
import { useProcessStructureStore } from '@/modules/tenant-admin/stores/tenant-admin-process-structure.store'
import { useTenantAdminUsersStore } from '@/modules/tenant-admin/stores/tenant-admin-users.store'
import { clearCachedCompany, clearPersistedCompanyAuth, getCachedCompanyId, getPersistedCompanyAuth, LOGOUT_REASONS, setLogoutReason, setPersistedCompanyAuth } from '@/shared/utils/company-cache.utils'
import { clearSessionExpiry, decodeJwtExpiryMs, persistSessionExpiry } from '@/shared/utils/session-token.utils'

function isSafariBrowser() {
  return /^(?:(?!chrome|android).)*safari/i.test(navigator.userAgent)
}

const SOURCE_TYPES = {
  ASSESSMENT: 'assessment',
  TRANSFORMATION: 'transformation',
}

const LOGIN_SCOPES = ['openid', 'profile', 'offline_access']

function createLoginRequest({ email, companyAuth }) {
  return {
    scopes: LOGIN_SCOPES,
    extraQueryParameters: companyAuth?.policy
      ? {
          p: companyAuth.policy,
          ...(companyAuth.company?.id && {
            domain_hint: companyAuth.company.id,
          }),
        }
      : {},
    loginHint: email?.trim()?.toLowerCase() ?? '',
    popupWindowAttributes: {
      width: 483,
      height: 600,
      top: 100,
      left: Math.max((window.screen.width - 483) / 2, 0),
    },
    redirectUri: `${window.location.origin}/login-processing`,
    redirectStartPage: `${window.location.origin}/login-processing`,
  }
}

function isInteractiveLoginRequired(error) {
  return error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError
}

function isMsalKey(key) {
  return key?.startsWith('msal.') || key?.includes('.b2clogin.com')
}

function clearMsalStorage() {
  const sessionKeys = []
  for (let index = 0; index < sessionStorage.length; index++) {
    const key = sessionStorage.key(index)
    if (isMsalKey(key))
      sessionKeys.push(key)
  }
  sessionKeys.forEach(key => sessionStorage.removeItem(key))

  const localKeys = []
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index)
    if (isMsalKey(key))
      localKeys.push(key)
  }
  localKeys.forEach(key => localStorage.removeItem(key))

  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim()
    if (isMsalKey(name))
      deleteCookie(name)
  })

  deleteCookie('accessToken')
  clearPersistedCompanyAuth()
}

function parseCompanyAuth() {
  try {
    const fromSession = sessionStorage.getItem('companyAuth')
    if (fromSession)
      return JSON.parse(fromSession)
    return getPersistedCompanyAuth()
  }
  catch (error) {
    console.error('Parsing error in companyAuth:', error)
    return null
  }
}

function isUtrakkCompanyAuth(companyAuth) {
  return companyAuth?.domainHint === UTRAKK_DOMAIN_HINT
}

function requireEnvironment(value, name) {
  if (typeof value === 'string' && value.trim())
    return value.trim()

  throw new Error(`Missing required environment variable: ${name}`)
}

export const useAuthStore = defineStore('auth', () => {
  const apiClient = inject('apiClient')
  const msalInstance = ref(null)
  const isLoading = ref(false)
  const isAuthenticated = ref(false)
  const isStartRevoke = ref(false)
  const isLoggingOut = ref(false)
  const tokenExchangePromise = ref(null)

  const userData = ref(null)
  const tenantData = ref(null)
  const companyData = ref(null)
  const companiesList = ref([])
  const switchContext = ref(null)
  const accessToken = ref(null)

  function captureAccessTokenFromExchange(payload) {
    const token = payload?.data?.accessToken ?? payload?.accessToken ?? null
    if (typeof token === 'string' && token.trim()) {
      accessToken.value = token
      persistSessionExpiry(decodeJwtExpiryMs(token))
    }
    return token
  }

  const isUserLoaded = computed(() => !!userData.value)
  const userPermissions = computed(
    () => userData.value?.permissionGroups || [],
  )
  const userRoles = computed(() => userData.value?.roles || [])
  const readOnlyRoles = computed(() => userData.value?.readOnlyRoles || [])
  const isPlatformAdmin = computed(() =>
    userRoles.value.some(role => PLATFORM_ADMIN_ROLES.includes(role)),
  )
  const isDigitalLeader = computed(() => !!userData.value?.isDigitalLeader)
  const isCrossCompanyContext = computed(() =>
    !isPlatformAdmin.value
    && switchContext.value !== null
    && switchContext.value.company !== companyData.value?.id,
  )

  async function initialize() {
    try {
      const userData = await fetchUserData()
      const companiesList = await fetchCompaniesList()
      return [userData, companiesList]
    }
    catch (error) {
      if (error?.response?.status === 401) {
        try {
          await refreshTokens()
          const [userData, companyData] = await Promise.all([fetchUserData(), fetchCompaniesList()])
          return [userData, companyData]
        }
        catch {
          if (!isLoggingOut.value) {
            await logout()
          }
        }
      }
      isAuthenticated.value = false
      return false
    }
  }

  async function initializeMsal({ consumeRedirect = true } = {}) {
    const rawAuthority = import.meta.env.VITE_AUTH_AUTHORITY
    const clientId = import.meta.env.VITE_AUTH_CLIENT_ID

    // If companyAuth is in sessionStorage, mirror it to localStorage so that
    // new tabs can initialize MSAL with the correct B2C authority without needing
    // to re-login. This is safe to call on every initializeMsal invocation.
    const rawSessionCompanyAuth = sessionStorage.getItem('companyAuth')
    if (rawSessionCompanyAuth) {
      try {
        const { policy, company } = JSON.parse(rawSessionCompanyAuth)
        setPersistedCompanyAuth({ policy, company })
      }
      catch {
        // ignore parse errors
      }
    }

    const companyAuthRaw = parseCompanyAuth()
    let policyFromStorage = ''
    const source = companyAuthRaw?.source
    const postLogoutRedirectUri = companyAuthRaw?.redirectUrl

    // Validate policy
    if (companyAuthRaw) {
      try {
        const companyAuth = companyAuthRaw
        // Get policy from session storage
        const rawPolicy = companyAuth?.policy || ''
        // Validate policy
        if (!rawPolicy) {
          policyFromStorage = ''
        }
        else if (/^[\w-]+$/.test(rawPolicy)) {
          policyFromStorage = rawPolicy
        }
        else {
          console.warn('Invalid policy ignoring...')
        }
      }
      catch (error) {
        console.error('Parsing error in companyAuth:', error)
      }
    }

    // Validate Authority
    let authority = rawAuthority

    if (policyFromStorage) {
      // Extract the policy if exists in URL
      const match = rawAuthority.match(/\/([\w-]+)$/)
      const currentPolicyInAuthority = match?.[1] || ''
      // If match...
      if (currentPolicyInAuthority === policyFromStorage) {
        // Authority have the same policy, keeping...
        authority = rawAuthority
      }
      else {
        // Remove wrong policy if exists and append the session policy
        // Remove / in the end too
        const baseAuthority = rawAuthority.replace(/\/[\w-]+$/, '').replace(/\/+$/, '')
        authority = `${baseAuthority}/${policyFromStorage}`
      }
    }

    const msalConfig = {
      auth: {
        clientId,
        authority,
        knownAuthorities: [import.meta.env.VITE_AUTH_KNOWN_AUTHORITIES],
        redirectUri: `${window.location.origin}/login-processing`,
        postLogoutRedirectUri:
          source === SOURCE_TYPES.ASSESSMENT
            ? postLogoutRedirectUri
            : `${window.location.origin}/login`,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: isSafariBrowser(),
      },
      system: {
        allowRedirectInIframe: true,
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 6000,
      },
    }

    msalInstance.value = new PublicClientApplication(msalConfig)
    await msalInstance.value.initialize()

    if (consumeRedirect && window.location.hash) {
      try {
        await msalInstance.value.handleRedirectPromise()
      }
      catch (redirectError) {
        console.error(redirectError)
      }
    }

    const accounts = msalInstance.value.getAllAccounts()
    isAuthenticated.value = accounts.length > 0

    return msalInstance.value
  }

  async function initializeUtrakkMsal({ consumeRedirect = true } = {}) {
    const redirectUri = resolveUtrakkLoginRedirectUri()
    const msalConfig = {
      auth: {
        clientId: requireEnvironment(
          import.meta.env.VITE_UTRAKK_AUTH_CLIENT_ID,
          'VITE_UTRAKK_AUTH_CLIENT_ID',
        ),
        authority: requireEnvironment(
          import.meta.env.VITE_UTRAKK_AUTH_AUTHORITY,
          'VITE_UTRAKK_AUTH_AUTHORITY',
        ),
        knownAuthorities: [
          requireEnvironment(
            import.meta.env.VITE_UTRAKK_AUTH_KNOWN_AUTHORITIES,
            'VITE_UTRAKK_AUTH_KNOWN_AUTHORITIES',
          ),
        ],
        redirectUri,
        postLogoutRedirectUri: `${window.location.origin}/login`,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: isSafariBrowser(),
      },
      system: {
        allowRedirectInIframe: true,
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
        loadFrameTimeout: 6000,
      },
    }

    msalInstance.value = new PublicClientApplication(msalConfig)
    await msalInstance.value.initialize()

    if (consumeRedirect && window.location.hash) {
      try {
        await msalInstance.value.handleRedirectPromise()
      }
      catch (redirectError) {
        console.error(redirectError)
      }
    }

    const accounts = msalInstance.value.getAllAccounts()
    isAuthenticated.value = accounts.length > 0

    return msalInstance.value
  }

  async function completeUtrakkRedirectLogin() {
    const companyAuth = parseCompanyAuth()

    if (!isUtrakkCompanyAuth(companyAuth)) {
      return false
    }

    await initializeUtrakkMsal({ consumeRedirect: false })

    const redirectResponse = await msalInstance.value.handleRedirectPromise()

    if (!redirectResponse?.idToken) {
      throw new Error('No ID token received')
    }

    try {
      await performTokenExchange(redirectResponse.idToken, {
        authProvider: UTRAKK_AUTH_PROVIDER,
      })
    }
    catch (error) {
      isAuthenticated.value = false
      throw error
    }

    return true
  }

  async function fetchUserData(force = false) {
    if (!force && isUserLoaded.value && userData.value) {
      return {
        user: userData.value,
        tenant: tenantData.value,
        company: companyData.value,
      }
    }

    const response = await apiClient('core').get('/auth/user')
    const { user, tenant, company } = response.data.data

    userData.value = user
    tenantData.value = tenant
    companyData.value = company

    isAuthenticated.value = true

    return response.data.data
  }

  async function performTokenExchange(idToken, { authProvider, company } = {}) {
    if (tokenExchangePromise.value) {
      return tokenExchangePromise.value
    }

    try {
      const companyAuth = parseCompanyAuth()
      const companyId = company?.id
        || company
        || companyData.value?.id
        || companyAuth?.company?.id

      if (!companyId && authProvider !== UTRAKK_AUTH_PROVIDER) {
        throw new Error('No company ID available')
      }

      const payload = {
        idToken,
        ...(companyId ? { company: companyId } : {}),
        ...(authProvider ? { authProvider } : {}),
      }

      tokenExchangePromise.value = apiClient('gatekeeper')
        .post('/token/exchange', payload)
        .then((response) => {
          isAuthenticated.value = true
          captureAccessTokenFromExchange(response.data)
          return response.data
        })

      return await tokenExchangePromise.value
    }
    finally {
      tokenExchangePromise.value = null
    }
  }

  async function refreshTokens() {
    if (!msalInstance.value) {
      await initializeMsal()
    }

    const currentAccount = msalInstance.value.getAllAccounts()[0]
    if (!currentAccount) {
      isAuthenticated.value = false
      throw new Error('No active account')
    }

    const response = await msalInstance.value.acquireTokenSilent({
      account: currentAccount,
      scopes: ['openid', 'profile', 'offline_access'],
    })

    if (!response?.idToken) {
      throw new Error('No ID token received')
    }

    return performTokenExchange(response.idToken)
  }

  async function revokeTransformationToken() {
    if (isStartRevoke.value)
      return

    try {
      isStartRevoke.value = true
      await apiClient('gatekeeper').post('/token/revoke')
    }
    catch (error) {
      if (error.response?.status === 401) {
        console.warn('[Auth] Token revoke returned 401 - Skipping retry')
        return
      }
      console.error('[Auth] Failed to revoke token:', error)
      throw error
    }
    finally {
      isStartRevoke.value = false
    }
  }

  async function login(data) {
    if (isLoading.value || tokenExchangePromise.value) {
      return false
    }

    try {
      isLoading.value = true

      if (!msalInstance.value) {
        await initializeMsal()
      }

      const companyAuth = JSON.parse(sessionStorage.getItem('companyAuth'))

      if (!companyAuth?.company) {
        throw new Error('No company selected')
      }

      const accounts = msalInstance.value.getAllAccounts()
      let response

      if (accounts.length > 0) {
        try {
          response = await msalInstance.value.acquireTokenSilent({
            scopes: ['openid', 'profile', 'offline_access'],
            account: accounts[0],
          })
        }
        catch (silentError) {
          console.error(silentError)
        }
      }

      if (!response) {
        const loginConfig = createLoginRequest({ email: data.email, companyAuth })

        try {
          response = await msalInstance.value.loginPopup(loginConfig)
        }
        catch (error) {
          if (error.name === 'BrowserAuthError' || error.errorCode === 'access_denied') {
            if (error.message.includes('user_cancelled') || error.errorCode === 'access_denied') {
              throw new Error('login_cancelled')
            }
            else if (['popup_window_error', 'empty_hash'].some(message => error.message.includes(message))) {
              await msalInstance.value.loginRedirect(loginConfig)
              return
            }
          }
          throw error
        }
      }

      if (!response?.idToken) {
        throw new Error('No ID token received')
      }

      const result = await performTokenExchange(response.idToken)
      return result
    }
    finally {
      isLoading.value = false
    }
  }

  async function loginWithUtrakk(rawEmail) {
    if (isLoading.value || tokenExchangePromise.value) {
      return false
    }

    try {
      isLoading.value = true

      sessionStorage.setItem(
        'companyAuth',
        JSON.stringify({
          domainHint: UTRAKK_DOMAIN_HINT,
        }),
      )

      msalInstance.value = null
      await initializeUtrakkMsal()

      await msalInstance.value.loginRedirect(createUtrakkMsalLoginRequest(rawEmail))
    }
    finally {
      isLoading.value = false
    }
  }

  async function logout({ redirectUrl } = {}) {
    if (isLoggingOut.value)
      return

    try {
      isLoggingOut.value = true
      isLoading.value = true

      if (isAuthenticated.value && !isStartRevoke.value) {
        try {
          await revokeTransformationToken()
        }
        catch (error) {
          if (error.response?.status === 401) {
            console.warn('[Logout] Token revoke returned 401 - Skipping retry')
          }
          else {
            console.error('[Logout] Failed to revoke token:', error)
          }
        }
      }

      const instanceToLogout = redirectUrl ? null : msalInstance.value

      msalInstance.value = null
      isAuthenticated.value = false
      isStartRevoke.value = false
      userData.value = null
      tenantData.value = null
      companyData.value = null
      accessToken.value = null
      clearSessionExpiry()
      sessionStorage.clear()
      clearMsalStorage()

      if (instanceToLogout) {
        await instanceToLogout.logout()
      }
      else if (!redirectUrl) {
        const instance = await initializeMsal()
        await instance.logout()
      }
    }
    finally {
      isLoading.value = false
      isLoggingOut.value = false
    }
  }

  function hasPermission(roles, { all = false } = {}) {
    const rolesToCheck = Array.isArray(roles) ? roles : [roles]
    if (!userRoles.value) {
      return false
    }
    return all
      ? rolesToCheck.every(role => userRoles.value.includes(role))
      : rolesToCheck.some(role => userRoles.value.includes(role))
  }

  async function validateEmail(email) {
    try {
      isLoading.value = true

      const { data } = await apiClient('core').post('/auth/validateEmail', {
        email,
      })

      if (!data.success) {
        throw new Error(data.message)
      }

      const { company, policy } = data.data

      if (!company) {
        throw new Error('No company data returned from email validation')
      }

      companyData.value = company

      sessionStorage.setItem(
        'companyAuth',
        JSON.stringify({
          company,
          policy,
          email,
        }),
      )

      setPersistedCompanyAuth({ policy, company })

      return data
    }
    finally {
      isLoading.value = false
    }
  }

  async function fetchCompaniesList() {
    try {
      isLoading.value = true
      const response = await apiClient('core').get('/identity/users/userTenants')
      const { tenants } = response.data

      if (!Array.isArray(tenants)) {
        throw new TypeError('Invalid response: tenants is not an array')
      }

      const companies = tenants.flatMap(tenant =>
        tenant.companies.map(company => ({
          tenantId: tenant._id,
          tenantName: tenant.name,
          tenantIdentifier: tenant.identifier,
          id: company._id,
          name: `${company.name} (${tenant.name})`,
        })),
      )

      companies.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      )

      companiesList.value = companies
      return response.data
    }
    finally {
      isLoading.value = false
    }
  }
  async function validateSelectedCompany(companySelected) {
    if (!companySelected?.id) {
      throw new Error('Invalid company selected: missing company ID')
    }

    if (companyData.value?.id === companySelected.id)
      return

    // Persist companyAuth to localStorage before switching. This ensures that
    // a new tab opened after this switch can initialize MSAL with the correct
    // B2C authority without requiring re-login.
    const rawSessionCompanyAuth = sessionStorage.getItem('companyAuth')
    if (rawSessionCompanyAuth) {
      try {
        const { policy, company } = JSON.parse(rawSessionCompanyAuth)
        setPersistedCompanyAuth({ policy, company })
      }
      catch {
        // ignore parse errors
      }
    }

    try {
      if (!parseCompanyAuth() && userData.value?.email) {
        await validateEmail(userData.value.email)
      }

      if (!msalInstance.value) {
        await initializeMsal()
      }

      const msalResponse = await acquireIdTokenForCompanySwitch()

      if (!msalResponse?.idToken) {
        throw new Error('No ID token received')
      }

      await performTokenExchange(msalResponse.idToken, { company: companySelected.id })

      companyData.value = companySelected
    }
    finally {
      isLoading.value = false
    }
  }

  async function acquireIdTokenForCompanySwitch() {
    const [currentAccount] = msalInstance.value.getAllAccounts()

    if (currentAccount) {
      try {
        return await msalInstance.value.acquireTokenSilent({
          account: currentAccount,
          scopes: LOGIN_SCOPES,
        })
      }
      catch (error) {
        if (!isInteractiveLoginRequired(error)) {
          throw error
        }
        console.warn('[Auth] Silent token acquisition failed, falling back to interactive login:', error)
      }
    }

    return msalInstance.value.loginPopup(
      createLoginRequest({ email: userData.value?.email, companyAuth: parseCompanyAuth() }),
    )
  }

  async function resolveMultiCompanyEntry() {
    // b2cId is the stable per-user identifier (same across company contexts),
    // unlike user.id which is scoped to the active company.
    const userId = userData.value?.b2cId
    const cachedId = userId ? getCachedCompanyId(userId) : null

    if (!cachedId)
      return 'select'

    const cached = companiesList.value.find(c => c.id === cachedId)

    if (cached) {
      await validateSelectedCompany(cached)
      await fetchUserData(true)
      return 'home'
    }

    clearCachedCompany(userId)

    if (companiesList.value.length > 0)
      return 'select'

    setLogoutReason(LOGOUT_REASONS.COMPANY_REVOKED)
    await logout()
    return 'logout'
  }

  async function createTokenToAssessment({ email }) {
    try {
      isLoading.value = true
      const response = await apiClient('core').post('/auth/redirect-token', { email })
      return response.data
    }
    finally {
      isLoading.value = false
    }
  }

  function updateCurrentCompanyShortcuts(companyId, { shortcuts, shortcutDisplayMode } = {}) {
    const currentCompanyId = companyData.value?.id
    if (!currentCompanyId || String(currentCompanyId) !== String(companyId)) {
      return false
    }

    const nextCompanyData = { ...companyData.value }

    if (Array.isArray(shortcuts)) {
      nextCompanyData.shortcuts = shortcuts
    }

    if (shortcutDisplayMode) {
      nextCompanyData.shortcutDisplayMode = shortcutDisplayMode
    }

    companyData.value = nextCompanyData

    return true
  }

  return {
    isLoading,
    isAuthenticated,
    isLoggingOut,
    isUserLoaded,
    userData,
    tenantData,
    companyData,
    userPermissions,
    hasPermission,
    userRoles,
    readOnlyRoles,
    switchContext,
    isPlatformAdmin,
    isDigitalLeader,
    isCrossCompanyContext,
    msalInstance,
    setSwitchContext: (context) => {
      switchContext.value = context

      const tenantAdminUsersStore = useTenantAdminUsersStore()
      const businessStructureStore = useBusinessStructureStore()
      const physicalStructureStore = usePhysicalStructureStore()
      const processStructureStore = useProcessStructureStore()

      tenantAdminUsersStore.setUsersList([])
      businessStructureStore.clearWorkingNode()
      physicalStructureStore.clearWorkingNode()
      processStructureStore.clearWorkingNode()
    },
    clearSwitchContext: () => {
      switchContext.value = null
    },
    clearMsalStorage,
    resetMsalInstance: () => { msalInstance.value = null },
    login,
    loginWithUtrakk,
    completeUtrakkRedirectLogin,
    logout,
    accessToken,
    refreshTokens,
    fetchUserData,
    initialize,
    validateEmail,
    companiesList,
    fetchCompaniesList,
    validateSelectedCompany,
    resolveMultiCompanyEntry,
    createTokenToAssessment,
    updateCurrentCompanyShortcuts,
  }
})
